import config from "../../config";
import { getRegionContactInfo } from "../../util/contactRegion";

const activeContactInfo = getRegionContactInfo();

// Company data sourced from config.js — single source of truth
const COMPANY_DATA = {
  name: activeContactInfo?.companyName || config.companyFullName,
  gstin: config.isIndia ? "36AALCT2102L1ZB" : "",
  cin: config.isIndia ? "U62090TS2024PTC188514" : "",
  logoPath: "/assets/images/thurent-logo.svg",
  address: config.isIndia
    ? "T-Hub Phase II Gachibowli"
    : activeContactInfo?.address || "Manfield, TX",
  city: config.isIndia ? "Hyderabad" : "",
  state: config.isIndia ? "Telangana" : "",
  pincode: config.isIndia ? "500060" : "",
  mobile: activeContactInfo?.phone || "",
  email: activeContactInfo?.email || "",
  website: config.isIndia ? "https://thurent.in" : "https://thurent.us",
  accountName: "THURENT TECH SERVICES PRIVATE LIMITED",
  accountNumber: "12345678901",
  bankName: "STATE BANK OF INDIA",
  ifscCode: "SBIN0001234",
  bankBranch: "Main Branch",
};

// Helper: count inclusive days between two date strings
const countDays = (start, end) => {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e)) return null;
  const diff = Math.round((e - s) / (1000 * 60 * 60 * 24));
  return diff >= 1 ? diff + 1 : 1; // inclusive (start day + end day)
};

export const InvoiceGenerator = {
  cleanProviderName(name) {
    if (!name) return "";
    return name.replace(/\s+null$/i, "").trim();
  },

  companyData() {
    return COMPANY_DATA;
  },

  // Generate Invoice Number  INV-YYYYMMDD-HHMMSS-eventId
  generateInvoiceNumber(eventId) {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, "");
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, "");
    return `INV-${dateStr}-${timeStr}-${eventId}`;
  },

  // ================= ESTIMATE =================
  // eventCart shape (built in Cartbody.js and EventTabs):
  // {
  //   event: { id, eventTitle, eventType, startDate, venue },
  //   cateringProviders: cartbodyslice.cart[id].cateringData,
  //   tentProviders:     cartbodyslice.cart[id].tentHouseData,
  //   priestProviders:   cartbodyslice.cart[id].priestData,
  //   venueProviders:    cartbodyslice.cart[id].venueData,
  // }
  generateEstimateInvoice(eventCart) {
    let items = [];
    let paymentSummarys = [];
    let subtotal = 0;
    let totalTax = 0;
    let paidAmount = 0;
    let pendingAmount = 0;

    // Event-level dates drive day-based multiplication for daily services
    // (catering, tent) — computed up front so every section below can use them.
    const ev = eventCart.event || {};
    const eventStartDate = ev.startDate || ev.Date || ev.start_date || "";
    const eventEndDate = ev.endDate || ev.end_date || ev.EndDate || "";
    const eventNumDays = countDays(eventStartDate, eventEndDate) || 1;

    // ================= CATERING =================
    // Redux fields (usecateringcart.js): provider.providername, service.total_amount,
    // service.gst_amount, service.mealtype, service.quantity, service.category
    // NOTE: total_amount / gst_amount are PER-DAY rates — multiplied by eventNumDays
    // below, since catering is billed per day of the event.
    (eventCart.cateringProviders || []).forEach((provider) => {
      if (provider.paymentSummary) {
        paymentSummarys.push({ ...provider.paymentSummary, providerType: "Catering Services" });
      }
      paidAmount += provider.paid_amount || 0;
      pendingAmount += provider.balance_to_be_paid || 0;

      (provider.services || []).forEach((item) => {
        const perDayTotal = item.total_amount || 0;
        const perDayTax = item.gst_amount || 0;
        const itemTotal = perDayTotal * eventNumDays;
        const itemTax = perDayTax * eventNumDays;
        const baseAmount = itemTotal - itemTax;

        // usecateringcart stores mealtype (string), not meal.name object
        const mealName = item.mealtype || item.meal?.name || "";
        const guestCount = item.quantity || item.number_of_people || 1;
        const description = mealName
          ? `${mealName} • ${guestCount} Guests`
          : `Catering • ${guestCount} Guests`;

        items.push({
          providerName: this.cleanProviderName(provider.providername),
          serviceType: "catering",
          description,
          amount: baseAmount,
          tax: itemTax,
          total: itemTotal,
          details: {
            mealType: mealName,
            numberOfPeople: guestCount,
            category: item.category || item.category?.name,
            gstPercentage: item.gst_percentage,
            numDays: eventNumDays,
            fromDate: eventStartDate,
            toDate: eventEndDate,
            perDayAmount: perDayTotal,
          },
        });

        subtotal += baseAmount;
        totalTax += itemTax;
      });
    });

    // ================= TENT =================
    // Pricing on the tent provider (providerPerDayTotal / providerPerDayTax) is a
    // PER-DAY rate — multiplied by eventNumDays below, since tent house is billed
    // per day of the event. Always uses the event's start/end dates.
    (eventCart.tentProviders || []).forEach((provider) => {
      if (provider.paymentSummary != null) {
        paymentSummarys.push({ ...provider.paymentSummary, providerType: "Tent House Services" });
      }
      paidAmount += provider.paidAmount ?? provider.paid_amount ?? 0;
      pendingAmount += provider.balanceToBePaid ?? provider.balance_to_be_paid ?? 0;

      const providerPerDayTotal = provider.pricing?.totalAmount ?? provider.pricing?.total_amount ?? provider.totalAmount ?? provider.total_amount ?? provider.total ?? 0;
      const providerPerDayTax = provider.pricing?.gstAmount ?? provider.pricing?.gst_amount ?? provider.gstAmount ?? provider.gst_amount ?? 0;
      const hasPricingObj = providerPerDayTotal > 0 || providerPerDayTax > 0;

      const services = provider.services || provider.items || [];
      const descLines = services.map(s =>
        `${s.serviceName || s.service_name || s.service_id || "Item"}: ×${s.quantity ?? 1}`
      );

      if (hasPricingObj) {
        const total = providerPerDayTotal * eventNumDays;
        const tax = providerPerDayTax * eventNumDays;
        const base = total - tax;

        const description = descLines.length > 0
          ? descLines.join(", ")
          : (provider.isPackage || provider.is_package
            ? `Package: ${provider.package?.packageName || provider.package?.package_name || "Tent Package"}`
            : "Tent House");

        items.push({
          providerName: this.cleanProviderName(provider.providername || provider.providerName),
          serviceType: "tent",
          description,
          amount: base,
          tax,
          total,
          details: {
            isPackage: !!(provider.isPackage || provider.is_package),
            gstPercentage: provider.pricing?.gstPercentage ?? provider.pricing?.gst_percentage ?? 0,
            numDays: eventNumDays,
            fromDate: eventStartDate,
            toDate: eventEndDate,
            perDayAmount: providerPerDayTotal,
          },
        });
        subtotal += base;
        totalTax += tax;
      } else {
        // Fallback: sum per-service price × quantity — treated as a per-day rate too
        let perDayLineTotal = 0;
        services.forEach((s) => {
          perDayLineTotal += (s.price ?? s.servicePrice ?? s.service_price ?? 0) * (s.quantity ?? 1);
        });

        if (perDayLineTotal > 0) {
          const lineTotal = perDayLineTotal * eventNumDays;
          items.push({
            providerName: this.cleanProviderName(provider.providername || provider.providerName),
            serviceType: "tent",
            description: descLines.length > 0 ? descLines.join(", ") : "Tent House Services",
            amount: lineTotal,
            tax: 0,
            total: lineTotal,
            details: {
              isPackage: !!(provider.isPackage || provider.is_package),
              gstPercentage: 0,
              numDays: eventNumDays,
              fromDate: eventStartDate,
              toDate: eventEndDate,
              perDayAmount: perDayLineTotal,
            },
          });
          subtotal += lineTotal;
        }
      }
    });

    // ================= PRIEST =================
    // Redux fields (usePriestCart.js): item.totalAmount (flattened from pricing.totalAmount),
    // item.gstAmount (flattened from pricing.gstAmount), item.providername, item.price.
    (eventCart.priestProviders || []).forEach((item) => {
      if (item.paymentSummary) {
        paymentSummarys.push({ ...item.paymentSummary, providerType: "Priest Services" });
      }
      paidAmount += item.paid_amount || 0;
      pendingAmount += item.balance_to_be_paid || 0;

      const total = item.totalAmount ?? item.pricing?.totalAmount ?? item.price ?? 0;
      const tax = item.gstAmount ?? item.pricing?.gstAmount ?? 0;

      items.push({
        providerName: this.cleanProviderName(item.providername),
        serviceType: "priest",
        description: item.isPackage
          ? `Package: ${item.packageDetails?.packageName || "Priest Package"}`
          : "Priest Service",
        amount: total - tax,
        tax,
        total,
        details: {
          isPackage: item.isPackage,
          packageName: item.packageDetails?.packageName,
          gstPercentage: item.gstPercentage ?? item.pricing?.gstPercentage ?? 0,
        },
      });

      subtotal += total - tax;
      totalTax += tax;
    });

    // ================= VENUE =================
    // Redux fields (cartbodydataslice venueData): item.venue_name, item.provider_name,
    // item.total_amount, item.gst_amount, item.gst_percentage
    (eventCart.venueProviders || []).forEach((item) => {
      const total = parseFloat(item.total_amount || item.totalAmount || 0);
      const tax = parseFloat(item.gst_amount || item.gstAmount || 0);
      const base = total - tax;

      if (total > 0) {
        const venueName = item.venue_name || item.venue?.name || "";
        const sectionName = item.section_name || item.section?.name || "";
        const description = venueName
          ? `Venue: ${venueName}${sectionName ? ` – ${sectionName}` : ""}`
          : "Venue Booking";

        items.push({
          providerName: this.cleanProviderName(item.provider_name || item.providername || ""),
          serviceType: "venue",
          description,
          amount: base,
          tax,
          total,
          details: { gstPercentage: item.gst_percentage || 0 },
        });

        subtotal += base;
        totalTax += tax;
      }
    });

    // ================= FINAL =================
    return {
      type: "estimate",
      invoiceNumber: this.generateInvoiceNumber(ev.id),
      generatedDate: new Date(),
      eventId: ev.id,
      eventTitle: ev.eventTitle || ev.eventType,
      eventType: ev.eventType,
      eventDate: eventStartDate,
      eventEndDate: eventEndDate,
      numEventDays: eventNumDays,
      venue: ev.venue || ev.Location,
      items,
      paymentSummarys,
      subtotal,
      totalTax,
      grandTotal: subtotal + totalTax,
      paidAmount,
      pendingAmount,
      paymentStatus: "pending",
    };
  },

  // ================= PAYMENT INVOICE =================
  generatePaymentInvoice(eventCart) {
    const estimate = this.generateEstimateInvoice(eventCart);
    return {
      ...estimate,
      type: "invoice",
      invoiceNumber: this.generateInvoiceNumber(eventCart.event?.id),
      pendingAmount: estimate.grandTotal - (estimate.paidAmount || 0),
      paymentStatus:
        (estimate.paidAmount || 0) >= estimate.grandTotal ? "paid" : "partiallyPaid",
      paymentDate: new Date(),
      paymentSummarys: [],
    };
  },

  // ================= RECEIPT =================
  generateReceiptInvoice(eventCart) {
    const estimate = this.generateEstimateInvoice(eventCart);
    return {
      ...estimate,
      type: "receipt",
      invoiceNumber: this.generateInvoiceNumber(eventCart.event?.id),
    };
  },
};